/**
 * Toolbar del Action Center Premium (0666b).
 *
 * Search debounced + filtro por tipo (tabs) + counter total.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useI18n } from '../../../../i18n';
import type { ActionItemTipo, VistaActionCenter } from '../hooks/useActionCenterList';

// SVG inline premium (regla frontend.md · NUNCA emojis · NUNCA librerías externas).
const ICON_ALL = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="12" height="2.5" rx="0.5" />
    <rect x="2" y="7" width="12" height="2.5" rx="0.5" />
    <rect x="2" y="11" width="12" height="2.5" rx="0.5" />
  </svg>
);
const ICON_ORG = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="6" y="2" width="4" height="3" rx="0.5" />
    <rect x="2" y="11" width="4" height="3" rx="0.5" />
    <rect x="10" y="11" width="4" height="3" rx="0.5" />
    <path d="M8 5v3M4 8h8M4 8v3M12 8v3" />
  </svg>
);
const ICON_DOC = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 2h6l4 4v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
    <path d="M9 2v4h4" />
    <path d="M5 9h6M5 12h6" />
  </svg>
);
const ICON_FLOW = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="4" height="4" rx="0.5" />
    <rect x="10" y="2" width="4" height="4" rx="0.5" />
    <rect x="6" y="10" width="4" height="4" rx="0.5" />
    <path d="M4 6v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6" />
    <path d="M8 9v1" />
  </svg>
);
const ICON_REQUEST = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="10" height="11" rx="1" />
    <path d="M5 7l1.5 1.5L10 5" />
    <path d="M5 11h6" />
  </svg>
);
const ICON_BUY = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 3h2l1.5 8a1 1 0 0 0 1 0.8h6a1 1 0 0 0 1-0.8L15 5H4" />
    <circle cx="6.5" cy="13.5" r="0.8" />
    <circle cx="11.5" cy="13.5" r="0.8" />
  </svg>
);

export interface ActionCenterToolbarProps {
  vista: VistaActionCenter;
  onVistaChange: (vista: VistaActionCenter) => void;
  q: string;
  onQChange: (q: string) => void;
  tipo: ActionItemTipo | 'all';
  onTipoChange: (tipo: ActionItemTipo | 'all') => void;
  counts: Record<ActionItemTipo, number>;
  total: number;
}

const VISTAS: ReadonlyArray<VistaActionCenter> = ['PENDIENTES', 'EN_CURSO', 'HISTORICO'] as const;

const TABS: ReadonlyArray<ActionItemTipo | 'all'> = [
  'all',
  'ORGANIGRAMA',
  'DOC',
  'FLUJOGRAMA',
  'APROBACION_PETICION',
  'APROBACION_COMPRA',
] as const;

const TIPO_ICON: Record<ActionItemTipo | 'all', ReactNode> = {
  all: ICON_ALL,
  ORGANIGRAMA: ICON_ORG,
  DOC: ICON_DOC,
  FLUJOGRAMA: ICON_FLOW,
  APROBACION_DOCUMENTO: ICON_DOC,
  APROBACION_PETICION: ICON_REQUEST,
  APROBACION_COMPRA: ICON_BUY,
};

export function ActionCenterToolbar(props: ActionCenterToolbarProps) {
  const { t } = useI18n();
  const tt = t.sign.actionCenter.premium;
  const [localQ, setLocalQ] = useState(props.q);

  // Debounce 200ms para no batir el endpoint en cada tecla.
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (localQ !== props.q) props.onQChange(localQ);
    }, 200);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQ]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '12px 16px',
        borderBottom: '1px solid #E1E5EC',
        background: '#fff',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 18,
            fontWeight: 600,
            color: '#1F3864',
            margin: 0,
          }}
        >
          {tt.title}
        </h2>
        <span
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 12,
            color: '#6B7280',
            padding: '2px 8px',
            background: '#F3F4F6',
            borderRadius: 4,
          }}
        >
          {tt.countItems.replace('{n}', String(props.total))}
        </span>
        <div style={{ flex: 1 }} />
        <input
          type="search"
          value={localQ}
          onChange={(e) => setLocalQ(e.target.value)}
          placeholder={tt.searchPlaceholder}
          aria-label={tt.searchPlaceholder}
          style={{
            flex: '0 0 360px',
            padding: '8px 12px',
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 14,
            border: '1px solid #D1D5DB',
            borderRadius: 6,
            outline: 'none',
          }}
        />
      </div>
      {/* Segmented control de vistas — Pendientes / En curso / Histórico */}
      <div
        role="radiogroup"
        aria-label={tt.vistaLabel}
        style={{
          display: 'inline-flex',
          gap: 0,
          background: '#F3F4F6',
          padding: 3,
          borderRadius: 8,
          alignSelf: 'flex-start',
        }}
      >
        {VISTAS.map((v) => {
          const isActive = props.vista === v;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => props.onVistaChange(v)}
              style={{
                padding: '6px 14px',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 13,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? '#1F3864' : '#6B7280',
                background: isActive ? '#fff' : 'transparent',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                boxShadow: isActive ? '0 1px 3px rgba(15,23,42,0.08)' : 'none',
                transition: 'all 120ms ease',
              }}
            >
              {vistaLabel(v, tt)}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 4, overflowX: 'auto' }}>
        {TABS.map((tipo) => {
          const isActive = props.tipo === tipo;
          const count = tipo === 'all' ? props.total : (props.counts[tipo as ActionItemTipo] ?? 0);
          return (
            <button
              key={tipo}
              type="button"
              onClick={() => props.onTipoChange(tipo)}
              style={{
                padding: '6px 12px',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 13,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? '#1F3864' : '#6B7280',
                background: isActive ? '#E8F0FE' : 'transparent',
                border: '1px solid',
                borderColor: isActive ? '#1F3864' : 'transparent',
                borderRadius: 6,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginRight: 6,
                  color: isActive ? '#1F3864' : '#6B7280',
                }}
                aria-hidden
              >
                {TIPO_ICON[tipo]}
              </span>
              {tipo === 'all' ? tt.tabAll : tipoLabel(tipo, tt)}
              {count > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 11,
                    color: isActive ? '#1F3864' : '#9CA3AF',
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Lee t.sign.actionCenter.premium.tipo* (ES/EN/ZH paridad). i18n obligatorio
// para texto visible · regla frontend.md § i18n. Antes hardcoded → bug que el
// CEO detectó en cambio idioma a ZH (las pestañas seguían en español).
function tipoLabel(
  tipo: ActionItemTipo,
  tt: {
    tipoOrganigrama: string;
    tipoDoc: string;
    tipoFlujograma: string;
    tipoDocLegacy: string;
    tipoPeticion: string;
    tipoCompra: string;
  },
): string {
  if (tipo === 'ORGANIGRAMA') return tt.tipoOrganigrama;
  if (tipo === 'DOC') return tt.tipoDoc;
  if (tipo === 'FLUJOGRAMA') return tt.tipoFlujograma;
  if (tipo === 'APROBACION_DOCUMENTO') return tt.tipoDocLegacy;
  if (tipo === 'APROBACION_PETICION') return tt.tipoPeticion;
  if (tipo === 'APROBACION_COMPRA') return tt.tipoCompra;
  return tipo;
}

// 0669 — labels de las 3 vistas temporales del Action Center.
function vistaLabel(
  vista: VistaActionCenter,
  tt: { vistaPendientes: string; vistaEnCurso: string; vistaHistorico: string },
): string {
  if (vista === 'PENDIENTES') return tt.vistaPendientes;
  if (vista === 'EN_CURSO') return tt.vistaEnCurso;
  return tt.vistaHistorico;
}
